import React, { useState } from "react";
import { Layout, Switch, Popover, Button, notification, Tooltip } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { Layers3, Route } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTender } from "../../contexts/TenderContext";
import { useJobTypeVersionStatus } from "../../hooks/useJobTypeVersionStatus";
import { useOpportunities, OPPORTUNITIES_QUERY_KEYS } from "../../hooks/useOpportunities";
import { opportunitiesApi } from "../../api/opportunities";
import MJ_LOGO from "@/assets/mnj-logo.jpg";
import AuthButton from "../auth/AuthButton";
import { EnvironmentBadge } from "./EnvironmentBadge";
import { useDevMode } from "@/contexts/DevModeContext";

type JobTypeOpportunity = {
  no?: number;
  clientName: string;
  region: string;
  name: string;
  siteAddress: string;
};

// Click-to-reveal Popover showing a single job type's own opportunity
// details (sourced from state.opportunity.groundworks/roadsAndSewers -
// populated for both imported and manually-entered job types, see
// TenderDetails.tsx's buildOpportunityField) - the Groundworks pill shows
// only opportunity.groundworks, the Roads & Sewers pill shows only
// opportunity.roadsAndSewers. Rendered as a labelled pill (icon + job type
// name + opportunity name) rather than a bare icon, so it's clear at a
// glance what it represents without needing to hover or click first.
const OpportunityIcon: React.FC<{
  jobType: "groundworks" | "roadsAndSewers";
  opportunity: JobTypeOpportunity;
  accentColor: string;
  variant: "dark" | "light";
  versionStatus?: string;
  isVersionStatusLoading?: boolean;
  groundworksTenderTotal?: number;
}> = ({ jobType, opportunity, accentColor, variant, versionStatus, isVersionStatusLoading, groundworksTenderTotal }) => {
  const isGroundworks = jobType === "groundworks";
  const label = isGroundworks ? "Groundworks" : "Roads & Sewers";
  const Icon = isGroundworks ? Layers3 : Route;
  const isDark = variant === "dark";
  const textColor = isDark ? "#ffffff" : accentColor;
  const pillBackground = isDark ? "rgba(255, 255, 255, 0.15)" : `${accentColor}14`;
  return (
    <Popover
      title={`${label} Opportunity`}
      trigger="click"
      content={
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 220, fontSize: 13 }}>
          <span><strong>Name:</strong> {opportunity.name || "-"}</span>
          <span><strong>Client:</strong> {opportunity.clientName || "-"}</span>
          <span><strong>Site:</strong> {opportunity.siteAddress || "-"}</span>
          <span><strong>Region:</strong> {opportunity.region || "-"}</span>
          {opportunity.no !== undefined && (
            <span><strong>Opp No:</strong> {opportunity.no}</span>
          )}
          <span>
            <strong>Status:</strong>{" "}
            {isVersionStatusLoading ? "Loading..." : versionStatus || "Pending"}
          </span>
          <ReSyncToBcButton groundworksTenderTotal={groundworksTenderTotal} />
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "5px",
          width: "150px",
          padding: "3px 12px",
          borderRadius: "999px",
          background: pillBackground,
          cursor: "pointer",
          lineHeight: "normal",
        }}
      >
        <Icon size={13} color={textColor} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: "12px", fontWeight: 600, color: textColor, whiteSpace: "nowrap" }}>
          {label}
        </span>
      </div>
    </Popover>
  );
};

const { Header: AntHeader } = Layout;

// const isLocalhost = typeof window !== 'undefined' &&
//   (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const isLocalhost = true;

interface HeaderProps {
  style?: React.CSSProperties;
  /** Live groundworks final tender total, adjustments included (MainLayout's own
   *  `useGroundworksTotalCalculations().tenderTotal`) — threaded down for
   *  ReSyncToBcButton so its `salesLCY` matches both the original Mark as Won
   *  call and the Groundworks Sum Analysis tab's own "Tender Total". See that
   *  component for why it can't just read `state.groundworks.totals` instead. */
  groundworksTenderTotal?: number;
}

// Dev-only Edit-mode switch. `state.isViewMode === true` means the app is
// locked into read-only mode. Toggling the Switch ON puts the app into Edit
// mode (`isViewMode = false`); OFF returns to View mode (`isViewMode = true`).
// Suppressed entirely in production builds via `showDevMode`. Reused by both
// header variants (old blue / new white) so the control follows the user
// across every page that renders the Header.
const DevEditModeSwitch: React.FC<{ textColor: string }> = ({ textColor }) => {
  const { state, setViewMode } = useTender();
  const showDevMode = useDevMode();
  if (!(showDevMode && isLocalhost)) return null;
  const isEditMode = !(state.isViewMode ?? false);
  return (
    <></>
    // <Tooltip title="Dev only — toggle View ↔ Edit mode">
    //   <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    //     <span style={{ fontSize: 12, color: textColor, lineHeight: 1 }}>
    //       {isEditMode ? 'Edit' : 'View'}
    //     </span>
    //     <Switch
    //       size="small"
    //       checked={isEditMode}
    //       onChange={(checked) => setViewMode(!checked)}
    //     />
    //   </div>
    // </Tooltip>
  );
};

// Shown only when the open tender's own workflow status is "Won" but its
// linked BC opportunity hasn't actually been closed as Won there yet — e.g.
// performMarkAsWon's BC close call (opportunitiesApi.closeOpportunity, see
// ReviewCalculate.tsx/GroundworksReview.tsx) failed or was skipped, leaving
// the tender and BC out of sync. Lets the user retry just the BC side
// (re-calling the same closeOpportunity endpoint) without re-running the
// whole Mark as Won save — the tender itself is already Won. Reused by both
// header variants, same convention as DevEditModeSwitch above.
const ReSyncToBcButton: React.FC<{ groundworksTenderTotal?: number }> = ({ groundworksTenderTotal }) => {
  const { state } = useTender();
  const queryClient = useQueryClient();
  const [isResyncing, setIsResyncing] = useState(false);
  const { data: opportunitiesData } = useOpportunities();

  const isTenderWon = (state.boqStatus || state.status || "").trim().toLowerCase() === "won";
  // Same match convention as performMarkAsWon's own opportunitiesData lookup.
  const matchedOpportunity = opportunitiesData?.find((opp) => opp.tender === state.projectName);
  const isBcOutOfSync =
    isTenderWon && !!matchedOpportunity && (matchedOpportunity.status || "").trim().toLowerCase() !== "won";

  if (!isBcOutOfSync || !matchedOpportunity) return null;

  const handleResync = async () => {
    setIsResyncing(true);
    try {
      // roadsAndSewersTotals.grandTotal is misleadingly named but IS the final,
      // discount-adjusted total (useRoadsAndSewersTotalSync's `finalTotal`) —
      // kept in sync on every page via MainLayout's always-mounted
      // useRoadsAndSewersTotalSync, so state's copy is reliable.
      // Groundworks has no equivalent context sync: state.groundworks.totals
      // is only ever written by GroundworksReview.tsx's "Save" button
      // (handleSaveBillSummary) — performMarkAsWon itself never persists it —
      // so it can be stale/0 here. Use groundworksTenderTotal instead (prop,
      // sourced from MainLayout's live useGroundworksTotalCalculations().tenderTotal)
      // — the fully adjusted (preliminaries/OH&P/discount) total, same value
      // performMarkAsWon sends as salesLCY and the same one the Groundworks
      // Sum Analysis tab shows as its own "Tender Total". Do NOT use
      // .grandTotal here — that's the pre-adjustment base total and would
      // under-report salesLCY whenever any of those adjustments apply.
      const roadsAndSewersGrandTotal = state.roadsAndSewersTotals?.grandTotal || 0;
      const tenderTotal = roadsAndSewersGrandTotal + (groundworksTenderTotal || 0);

      await opportunitiesApi.closeOpportunity(matchedOpportunity.tenderId, {
        actionTaken: "WON",
        closeOpportunityCode: "W-WON",
        closingDate: new Date().toISOString().split("T")[0],
        salesLCY: tenderTotal,
        cancelOldTasks: false,
      });

      queryClient.invalidateQueries({ queryKey: OPPORTUNITIES_QUERY_KEYS.all });

      notification.success({
        message: "Re-Synced to BC",
        description: "The opportunity has been re-synced as Won in Business Central.",
        duration: 5,
        placement: "topRight",
      });
    } catch (error: any) {
      const rawMessage =
        error?.response?.data?.error?.message ||
        error?.message ||
        "An error occurred while re-syncing to BC.";
      const cleanMessage = String(rawMessage).replace(/\s*CorrelationId:.*$/i, "").trim();
      notification.error({
        message: "Re-Sync to BC Failed",
        description: cleanMessage,
        duration: 8,
        placement: "topRight",
      });
    } finally {
      setIsResyncing(false);
    }
  };

  return (
    <Button
      size="small"
      type="primary"
      icon={<SyncOutlined spin={isResyncing} />}
      loading={isResyncing}
      onClick={handleResync}
      style={{ backgroundColor: "#faad14", borderColor: "#faad14", color: "#000", marginTop: 4, alignSelf: "flex-start" }}
    >
      Re-Sync to BC
    </Button>
  );
};

const Header: React.FC<HeaderProps> = ({ style, groundworksTenderTotal }) => {
  const { state } = useTender();
  const location = useLocation();

  // A selected job type only gets its own `state.opportunity.<jobType>` entry
  // once that job type's card has actually been imported/typed into on the
  // Tender Details step (see TenderDetails.tsx's buildOpportunityField) -
  // tenders that skip that (e.g. the legacy /tender-info flow, or a card
  // left untouched) never get it set, which previously meant the navbar pill
  // silently never rendered for that job type. Fall back to the tender's
  // shared projectName/client/siteAddress/region/opportunityNo - always
  // populated once a tender exists - so a selected job type always shows
  // *something* here instead of only when its own per-type entry happens to
  // be set.
  const fallbackOpportunity: JobTypeOpportunity | undefined = state.projectName
    ? {
        no: state.opportunityNo ? Number(state.opportunityNo) || undefined : undefined,
        clientName: state.client || "",
        region: state.region || "",
        name: state.projectName,
        siteAddress: state.siteAddress || "",
      }
    : undefined;
  const groundworksOpportunity = state.opportunity?.groundworks
    ?? (state.jobTypes?.groundworks ? fallbackOpportunity : undefined);
  const roadsAndSewersOpportunity = state.opportunity?.roadsAndSewers
    ?? (state.jobTypes?.roadsAndSewers ? fallbackOpportunity : undefined);

  const {
    groundworksVersionStatus,
    roadsAndSewersVersionStatus,
    isLoading: isVersionStatusLoading,
  } = useJobTypeVersionStatus(
    state.projectName || undefined,
    state.opportunityNo || undefined,
    !!groundworksOpportunity,
    !!roadsAndSewersOpportunity,
  );

  // Check if document library has a tender/project in context
  const isDocumentLibraryWithTender =
    location.pathname === "/document-library" && state.projectName;

  const isRateEngineWithoutProject =
    (location.pathname === "/rate-engine/rate-manager" || location.pathname === "/rate-engine/activity-tree") &&
    !state.projectName;

  // Use old header design for tender-info, new-tender, job-types, job-selection,
  // the /tender/* new-tender flow (job-types/import-opportunities/import-takeoff),
  // document-library when no tender is selected in context, rate-engine pages
  // without a project, and takeoff-configuration (matches Regional Rate
  // Library's navy header)
  const useOldHeader =
    location.pathname === "/tender-info" ||
    location.pathname === "/new-tender" ||
    location.pathname === "/job-types" ||
    location.pathname === "/job-selection" ||
    location.pathname === "/tender/job-types" ||
    location.pathname === "/tender/import-opportunities" ||
    location.pathname === "/tender/import-takeoff" ||
    location.pathname === "/takeoff-configuration" ||
    (location.pathname === "/document-library" && !isDocumentLibraryWithTender) ||
    isRateEngineWithoutProject;

  if (useOldHeader) {
    // Old Header Design (Blue background with logo)
    return (
      <AntHeader
        className="bg-colorPrimary"
        style={{
          ...style,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          padding: "12px 24px",
          height: "88px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              height: "100%",
            }}
          >
            <Link
              to="/opportunities"
              style={{
                display: "block",
                height: "64px",
                textDecoration: "none",
              }}
            >
              <img
                src={MJ_LOGO}
                alt="MNJ Construction Logo"
                style={{
                  height: "100%",
                  width: "auto",
                  maxWidth: "240px",
                  objectFit: "contain",
                }}
              />
            </Link>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "6px",
                lineHeight: "normal",
                minWidth: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    color: "#e2e8f0",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Estimator
                </span>
                <EnvironmentBadge mode={import.meta.env.MODE} variant="dark" />
              </div>
              {state.projectName && (
                <Tooltip title={state.projectName}>
                  <span
                    style={{
                      color: "#cbd5e1",
                      fontSize: "12px",
                      fontWeight: "600",
                      maxWidth: "320px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {state.projectName}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
            {(groundworksOpportunity || roadsAndSewersOpportunity) && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                {groundworksOpportunity && (
                  <OpportunityIcon
                    jobType="groundworks"
                    opportunity={groundworksOpportunity}
                    accentColor="#ffffff"
                    variant="dark"
                    versionStatus={groundworksVersionStatus}
                    isVersionStatusLoading={isVersionStatusLoading}
                    groundworksTenderTotal={groundworksTenderTotal}
                  />
                )}
                {roadsAndSewersOpportunity && (
                  <OpportunityIcon
                    jobType="roadsAndSewers"
                    opportunity={roadsAndSewersOpportunity}
                    accentColor="#ffffff"
                    variant="dark"
                    versionStatus={roadsAndSewersVersionStatus}
                    isVersionStatusLoading={isVersionStatusLoading}
                    groundworksTenderTotal={groundworksTenderTotal}
                  />
                )}
              </div>
            )}
            <DevEditModeSwitch textColor="#ffffff" />
            <AuthButton textColor="#ffffff" backgroundColor="transparent" />
          </div>
        </div>
      </AntHeader>
    );
  }

  // New Header Design (White background with icons)
  return (
    <AntHeader
      style={{
        ...style,
        backgroundColor: "#fff",
        borderBottom: "1px solid #e5e7eb",
        padding: "0 24px",
        height: "88px",
        display: "flex",
        alignItems: "center",
        lineHeight: "88px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {/* Left Side - Estimation Tool */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "6px",
            lineHeight: "normal",
            minWidth: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#000",
              }}
            >
              Estimator
            </span>
            <EnvironmentBadge mode={import.meta.env.MODE} />
          </div>
          {state.projectName && (
            <Tooltip title={state.projectName}>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#6b7280",
                  maxWidth: "320px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {state.projectName}
              </span>
            </Tooltip>
          )}
        </div>

        {/* Right Side - Opportunity Details and Auth */}
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          {(groundworksOpportunity || roadsAndSewersOpportunity) && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
              {groundworksOpportunity && (
                <OpportunityIcon
                  jobType="groundworks"
                  opportunity={groundworksOpportunity}
                  accentColor="#9333EA"
                  variant="light"
                  versionStatus={groundworksVersionStatus}
                  isVersionStatusLoading={isVersionStatusLoading}
                  groundworksTenderTotal={groundworksTenderTotal}
                />
              )}
              {roadsAndSewersOpportunity && (
                <OpportunityIcon
                  jobType="roadsAndSewers"
                  opportunity={roadsAndSewersOpportunity}
                  accentColor="#1890ff"
                  variant="light"
                  versionStatus={roadsAndSewersVersionStatus}
                  isVersionStatusLoading={isVersionStatusLoading}
                  groundworksTenderTotal={groundworksTenderTotal}
                />
              )}
            </div>
          )}
          <DevEditModeSwitch textColor="#000000" />
          <AuthButton textColor="#000000" />
        </div>
      </div>
    </AntHeader>
  );
};

export default Header;
